'use client';
import React, { useState, useEffect } from 'react';
import { Play, Pause, Radio, ChevronLeft, ChevronRight } from 'lucide-react';
import { streamService, Stream } from '@/lib/services/streamService';
import StreamPlayerModal from './StreamPlayerModal';

const CARDS_PER_PAGE = 6;

function formatListeners(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

interface RadioStationsRowProps {
  searchQuery?: string;
}

export default function RadioStationsRow({ searchQuery = '' }: RadioStationsRowProps) {
  const [playingStation, setPlayingStation] = useState<Stream | null>(null);
  const [stations, setStations] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    streamService.getRadioStations().then((data) => {
      setStations(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filtered = stations.filter((s) => {
    const q = searchQuery.toLowerCase().trim();
    return (
      !q ||
      s.name.toLowerCase().includes(q) ||
      s.country.toLowerCase().includes(q) ||
      (s.genre ?? '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / CARDS_PER_PAGE));
  const paginated = filtered.slice((currentPage - 1) * CARDS_PER_PAGE, currentPage * CARDS_PER_PAGE);

  return (
    <section>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2">
          <Radio size={18} className="text-white" />
          <h2 className="text-base sm:text-lg font-700 text-white">Radio Stations</h2>
          <span className="text-xs text-[rgb(115,115,115)] bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] px-2 py-0.5 rounded-full tabular-nums">
            {loading ? '...' : `${filtered.length} live`}
          </span>
        </div>
      </div>

      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pb-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`radio-skel-${i}`} className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl p-4 animate-pulse">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-full bg-[rgb(24,24,24)]" />
                <div className="w-8 h-8 rounded-full bg-[rgb(24,24,24)]" />
              </div>
              <div className="h-3 bg-[rgb(24,24,24)] rounded w-3/4 mb-2" />
              <div className="h-3 bg-[rgb(24,24,24)] rounded w-1/2" />
            </div>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <p className="text-sm text-[rgb(115,115,115)] py-4">
          {searchQuery ? `No radio stations matching "${searchQuery}".` : 'No radio stations available.'}
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {paginated.map((station) => {
              const isPlaying = playingStation?.id === station.id;
              return (
                <div
                  key={station.id}
                  className={`bg-[rgb(15,15,15)] border rounded-xl p-4 transition-all duration-150 cursor-pointer group ${
                    isPlaying
                      ? 'border-red-600 bg-[rgba(220,38,38,0.05)]'
                      : 'border-[rgb(38,38,38)] hover:border-[rgb(64,64,64)]'
                  }`}
                  onClick={() => setPlayingStation(isPlaying ? null : station)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${isPlaying ? 'bg-[rgba(220,38,38,0.15)]' : 'bg-[rgb(24,24,24)]'}`}>
                      {station.logoUrl ? (
                        <img
                          src={station.logoUrl}
                          alt={`${station.name} logo`}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <Radio size={18} className={isPlaying ? 'text-red-500' : 'text-[rgb(115,115,115)]'} />
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setPlayingStation(isPlaying ? null : station); }}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-95 ${isPlaying ? 'bg-white' : 'bg-red-600'}`}
                    >
                      {isPlaying
                        ? <Pause size={14} fill="black" className="text-black" />
                        : <Play size={14} fill="white" className="text-white ml-0.5" />
                      }
                    </button>
                  </div>
                  <p className="text-sm font-600 text-white truncate">{station.name}</p>
                  <p className="text-xs text-[rgb(115,115,115)] mt-0.5">{station.flag} {station.country}</p>
                  <p className="text-[10px] text-red-500 font-500 mt-1">{station.genre}</p>
                  {isPlaying && station.nowPlaying && (
                    <div className="mt-2 pt-2 border-t border-[rgb(38,38,38)]">
                      <p className="text-[10px] text-white truncate">▶ {station.nowPlaying}</p>
                    </div>
                  )}
                  <p className="text-[10px] text-[rgb(115,115,115)] mt-1.5 tabular-nums">
                    {formatListeners(station.listeners ?? 0)} listening
                  </p>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-[rgb(38,38,38)]"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={`radio-page-${page}`}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg text-sm font-600 transition-all ${
                    page === currentPage
                      ? 'bg-red-600 text-white' :'text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] border border-[rgb(38,38,38)]'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-[rgb(38,38,38)]"
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Stream player for radio */}
      {playingStation && (
        <StreamPlayerModal
          stream={playingStation}
          onClose={() => setPlayingStation(null)}
        />
      )}
    </section>
  );
}